<template>
  <div>
    <new-expense-wizard
      v-if="isSmallScreen"
      :expense-type-catalogs="expenseTypeCatalogs"
      :material-catalog-id="materialCatalogId"
      :supplier-options="supplierOptions"
      :grouped-expenses="groupedExpenses"
      :saving="saving"
      @refetch-categories="fetchExpensesTypeCatalogs($route.params.projectId)"
      @save="saveMobile"
    />

    <template v-else>
      <div class="d-flex align-items-center flex-wrap mb-2">
        <h1 class="font-weight-bolder mr-1 mb-0">
          {{ $t('new', { type: $t('expense') }) }}
        </h1>
        <span
          v-if="headerCategoryPath"
          class="text-muted"
        >{{ headerCategoryPath }}</span>
      </div>

      <validation-observer ref="expensesValidation">
        <b-form @submit.prevent="submit">
          <b-card class="mb-2">
            <div class="ticket-head">
              <div class="ticket-head__category">
                <div class="ticket-head__label">
                  {{ $t('expenseType') }}
                </div>
                <expense-type-menu
                  :index="0"
                  :expense-type-catalogs="expenseTypeCatalogs"
                  :record="header"
                  :history="headerHistory"
                  :show-leaf-notice="false"
                  @setExpenseType="onHeaderCategory"
                  @refetch="fetchExpensesTypeCatalogs($route.params.projectId)"
                />
                <div
                  v-if="headerCategoryName"
                  class="ticket-head__ready"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="14"
                    class="mr-25"
                  />
                  {{ $t('expenseCapture.willUse') }} <strong class="ml-25">{{ headerCategoryName }}</strong>
                </div>
              </div>

              <div class="ticket-head__fields">
                <b-form-group :label="$t('date')">
                  <b-form-datepicker
                    v-model="header.expense_date"
                    :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
                    :max="today"
                    locale="es"
                    hide-header
                    :label-no-date-selected="$t('expenseCapture.pickDate')"
                  />
                </b-form-group>

                <b-form-group
                  :label="$t('time')"
                  :description="$t('expenseCapture.timeHint')"
                >
                  <b-form-timepicker
                    v-model="header.expense_time"
                    locale="es"
                    :hour12="false"
                    hide-header
                    no-close-button
                    :label-no-time-selected="$t('expenseCapture.pickTime')"
                    :label-close="$t('close')"
                  />
                </b-form-group>

                <b-form-group :label="$t('supplier')">
                  <b-form-select
                    v-model="header.supplier"
                    :options="supplierOptions"
                  />
                  <validation-provider
                    v-if="header.supplier === 'Nuevo proveedor'"
                    #default="{ errors }"
                    :name="$t('supplier')"
                    rules="required"
                  >
                    <b-form-input
                      v-model="header.new_supplier"
                      class="mt-50"
                      :placeholder="$t('expenseCapture.newSupplierPlaceholder')"
                      :state="errors.length ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group :label="$t('status')">
                  <b-form-select
                    v-model="header.expense_status"
                    :options="statusOptions"
                  />
                </b-form-group>

                <b-form-group :label="`% ${$t('vat')}`">
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('vat')"
                    rules="required|min_value:0"
                  >
                    <b-form-input
                      v-model.number="header.iva_percentage"
                      type="number"
                      min="0"
                      step="0.01"
                      :state="errors.length ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </div>
            </div>
          </b-card>

          <b-card
            no-body
            class="mb-2"
          >
            <div class="lines-head">
              <div>
                <h4 class="mb-0">
                  {{ $t('expenseCapture.lines') }}
                </h4>
                <small class="text-muted">{{ $t('expenseCapture.linesHint') }}</small>
              </div>
              <span class="lines-head__count">{{ rows.length }}</span>
            </div>

            <b-alert
              :show="serverErrors.length > 0"
              variant="danger"
              class="mx-2 mt-1"
            >
              <div class="font-weight-bolder mb-25">
                {{ $t('expenseCapture.notSaved') }}
              </div>
              <ul class="mb-0 pl-1">
                <li
                  v-for="(message, i) in serverErrors"
                  :key="i"
                >
                  {{ message }}
                </li>
              </ul>
            </b-alert>

            <div class="lines-table-wrap">
              <table class="lines-table">
                <thead>
                  <tr>
                    <th class="lines-table__num">
                      #
                    </th>
                    <th>{{ $t('expenseCapture.materialOrConcept') }}</th>
                    <th class="lines-table__qty">
                      {{ $t('quantity') }}
                    </th>
                    <th class="lines-table__unit">
                      {{ $t('unit') }}
                    </th>
                    <th class="lines-table__price">
                      {{ $t('unitPrice') }}
                    </th>
                    <th class="lines-table__total">
                      {{ $t('total') }}
                    </th>
                    <th class="lines-table__actions" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in rows"
                    :key="row.uid"
                    :class="{ 'is-new-material': row.isNewMaterial }"
                  >
                    <td class="lines-table__num">
                      {{ index + 1 }}
                    </td>

                    <td>
                      <template v-if="isMaterialRow(row)">
                        <validation-provider
                          #default="{ errors }"
                          :name="`${$t('material')} ${index + 1}`"
                          rules="required"
                        >
                          <b-form-input
                            v-show="false"
                            v-model="row.name"
                          />
                          <div :class="['line-search', { 'is-invalid': errors.length }]">
                            <vue-bootstrap-typeahead
                              :ref="`search-${row.uid}`"
                              :value="row.query"
                              :placeholder="$t('expenseCapture.searchMaterial')"
                              :data="searchOptions(row)"
                              :serializer="item => item.name"
                              :show-all-results="true"
                              :max-matches="6"
                              @input="onQueryInput(row, $event)"
                              @hit="selectProduct(row, $event)"
                            >
                              <template
                                slot="suggestion"
                                slot-scope="{ data }"
                              >
                                <div
                                  v-if="data.isNewMaterialOption"
                                  class="line-search__suggestion line-search__new"
                                >
                                  <feather-icon
                                    icon="PlusCircleIcon"
                                    size="15"
                                    class="mr-50"
                                  />
                                  <span>{{ $t('expenseCapture.registerQueryAsNew', { query: data.name }) }}</span>
                                </div>
                                <div
                                  v-else
                                  class="line-search__suggestion"
                                >
                                  <span
                                    v-if="data.sku"
                                    class="line-search__sku"
                                  >{{ data.sku }}</span>
                                  <span>{{ data.name }}</span>
                                </div>
                              </template>
                            </vue-bootstrap-typeahead>
                          </div>
                          <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>

                        <div
                          v-if="row.name"
                          class="line-meta"
                        >
                          <span
                            v-if="row.isNewMaterial"
                            class="line-meta__new"
                          >
                            <feather-icon
                              icon="ClockIcon"
                              size="12"
                              class="mr-25"
                            />{{ $t('expenseCapture.pendingReview') }}
                          </span>
                          <span
                            v-else-if="row.sku"
                            class="line-meta__catalog"
                          >
                            <feather-icon
                              icon="CheckIcon"
                              size="12"
                              class="mr-25"
                            />{{ $t('expenseCapture.fromCatalog') }}
                          </span>
                          <span
                            v-if="row.brand"
                            class="text-muted"
                          >· {{ row.brand }}</span>
                        </div>

                        <div
                          v-else-if="canOfferNewMaterial(row)"
                          class="line-not-found"
                        >
                          <span>
                            {{ noMatches(row)
                              ? $t('materialNotFound', { query: row.query.trim() })
                              : $t('expenseCapture.noneOfThese') }}
                          </span>
                          <b-button
                            size="sm"
                            :variant="noMatches(row) ? 'primary' : 'outline-primary'"
                            @click="openNewMaterial(row)"
                          >
                            <feather-icon
                              icon="PlusIcon"
                              size="14"
                              class="mr-25"
                            />{{ $t('registerAsNewMaterial') }}
                          </b-button>
                        </div>
                      </template>

                      <validation-provider
                        v-else
                        #default="{ errors }"
                        :name="`${$t('expenseCapture.concept')} ${index + 1}`"
                        rules="required"
                      >
                        <b-form-input
                          :ref="`concept-${row.uid}`"
                          v-model="row.description"
                          :placeholder="$t('expenseCapture.conceptPlaceholder')"
                          :state="errors.length ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>

                      <button
                        type="button"
                        class="line-category"
                        @click="openRowCategory(row)"
                      >
                        <feather-icon
                          icon="TagIcon"
                          size="12"
                          class="mr-25"
                        />{{ effectiveCategoryName(row) || $t('expenseCapture.noCategory') }}
                        <span class="line-category__change">· {{ $t('expenseCapture.change') }}</span>
                      </button>
                    </td>

                    <td class="lines-table__qty">
                      <validation-provider
                        #default="{ errors }"
                        :name="`${$t('quantity')} ${index + 1}`"
                        rules="required|min_value:0.01"
                      >
                        <b-form-input
                          v-model.number="row.quantity"
                          type="number"
                          min="0"
                          step="any"
                          class="text-right"
                          :state="errors.length ? false : null"
                          @focus="selectAll"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </td>

                    <td class="lines-table__unit">
                      <b-form-select
                        v-if="isMaterialRow(row) && row.isNewMaterial"
                        v-model="row.unit_type"
                        :options="unitOptions"
                      />
                      <span
                        v-else
                        class="line-unit"
                      >{{ unitLabel(row) }}</span>
                    </td>

                    <td class="lines-table__price">
                      <validation-provider
                        #default="{ errors }"
                        :name="`${$t('unitPrice')} ${index + 1}`"
                        rules="required|min_value:0.01"
                      >
                        <b-input-group prepend="$">
                          <b-form-input
                            v-model.number="row.unit_price"
                            type="number"
                            min="0"
                            step="0.01"
                            class="text-right"
                            :state="errors.length ? false : null"
                            @focus="selectAll"
                            @keydown.enter.prevent="onPriceEnter(index)"
                          />
                        </b-input-group>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </td>

                    <td class="lines-table__total">
                      <span class="line-total">${{ rowTotal(row) | money }}</span>
                    </td>

                    <td class="lines-table__actions">
                      <button
                        type="button"
                        class="line-remove"
                        :title="$t('expenseCapture.removeLine')"
                        :disabled="rows.length === 1"
                        @click="removeRow(index)"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="16"
                        />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="lines-foot">
              <b-button
                variant="outline-primary"
                @click="addRow()"
              >
                <feather-icon
                  icon="PlusIcon"
                  size="14"
                  class="mr-25"
                />{{ $t('expenseCapture.addLine') }}
                <small class="ml-50 text-muted">{{ $t('expenseCapture.enterHint') }}</small>
              </b-button>

              <div class="totals">
                <div class="totals__row">
                  <span>{{ $t('subtotal') }}</span><span>${{ subtotal | money }}</span>
                </div>
                <div class="totals__row">
                  <span>{{ $t('vat') }} ({{ header.iva_percentage || 0 }}%)</span><span>${{ vat | money }}</span>
                </div>
                <div class="totals__row totals__row--grand">
                  <span>{{ $t('total') }}</span><span>${{ grandTotal | money }}</span>
                </div>
              </div>
            </div>
          </b-card>

          <div class="d-flex justify-content-between align-items-center flex-wrap">
            <b-button
              variant="flat-secondary"
              :disabled="saving"
              @click="cancel"
            >
              {{ $t('cancel') }}
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              size="lg"
              :disabled="saving"
            >
              <b-spinner
                v-if="saving"
                small
                class="mr-50"
              />
              {{ $tc('expenseCapture.saveLines', rows.length, { count: rows.length }) }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>

      <b-modal
        id="newMaterial"
        :title="$t('newMaterial')"
        :ok-title="$t('save')"
        :cancel-title="$t('cancel')"
        :ok-disabled="!newMaterialValid"
        centered
        @ok="confirmNewMaterial"
        @hidden="newMaterialRow = null"
      >
        <b-alert
          show
          variant="info"
          class="new-material__note"
        >
          <feather-icon
            icon="InfoIcon"
            size="16"
            class="mr-50 flex-shrink-0"
          />{{ $t('expenseCapture.newMaterialNote') }}
        </b-alert>
        <b-form-group :label="$t('name')">
          <b-form-input
            v-model="newMaterial.name"
            autocomplete="off"
            autofocus
          />
        </b-form-group>
        <b-form-group :label="$t('unit')">
          <b-form-select
            v-model="newMaterial.unit_type"
            :options="unitOptions"
          />
          <small class="text-muted">{{ $t('expenseCapture.unitHint') }}</small>
        </b-form-group>
        <b-form-group :label="$t('brand')">
          <b-form-input
            v-model="newMaterial.brand"
            autocomplete="off"
          />
        </b-form-group>
        <b-form-group
          :label="$t('description')"
          class="mb-0"
        >
          <b-form-input
            v-model="newMaterial.description"
            autocomplete="off"
          />
        </b-form-group>
      </b-modal>

      <b-modal
        id="rowCategory"
        :title="$t('expenseCapture.rowCategoryTitle')"
        :ok-title="$t('expenseCapture.useThisCategory')"
        :cancel-title="$t('cancel')"
        :ok-disabled="!rowCategoryDraft.expense_type_catalog_id"
        size="lg"
        centered
        @ok="confirmRowCategory"
        @hidden="rowCategoryRow = null"
      >
        <expense-type-menu
          v-if="rowCategoryRow"
          :index="1"
          :expense-type-catalogs="expenseTypeCatalogs"
          :record="rowCategoryDraft"
          :history="rowCategoryHistory"
          :allow-manage="false"
          @setExpenseType="name => { rowCategoryDraftName = name }"
        />
      </b-modal>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'underscore'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, minValue } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ExpenseTypeMenu from './ExpenseTypeMenu.vue'
import NewExpenseWizard from './NewExpenseWizard.vue'
import { todayLocalDate, nowLocalTime, expenseDateForApi } from '@/utils/expenseDates'

const UNIT_KEYS = ['piece', 'bundle', 'box', 'kilogram', 'ton', 'meter', 'square_meters', 'cubic_meters', 'trip']

let rowSequence = 0

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    ExpenseTypeMenu,
    NewExpenseWizard,
  },

  data() {
    return {
      required,
      minValue,
      saving: false,
      serverErrors: [],
      materialCatalogId: null,
      suppliersResults: [],
      today: todayLocalDate(),

      header: {
        expense_type_catalog_id: null,
        expense_date: todayLocalDate(),
        expense_time: nowLocalTime(),
        supplier: 'YoConstruyo',
        new_supplier: '',
        expense_status: 'paid',
        iva_percentage: 16,
      },
      headerHistory: [],
      headerCategoryName: '',

      rows: [],

      newMaterial: {
        name: '', unit_type: null, brand: '', description: '',
      },
      newMaterialRow: null,

      rowCategoryRow: null,
      rowCategoryDraft: { expense_type_catalog_id: null },
      rowCategoryHistory: [],
      rowCategoryDraftName: '',
    }
  },

  computed: {
    ...mapGetters('groupedExpenses', ['expenseTypeCatalogs', 'groupedExpenses']),
    ...mapGetters('app', ['unitsFilter', 'isSmallScreen']),

    headerCategoryPath() {
      return this.headerHistory.map(c => c && c.name).filter(Boolean).join(' › ')
    },

    supplierOptions() {
      const all = Array.from(new Set(['YoConstruyo', ...this.suppliersResults]))
      const opts = all.map(v => ({ value: v, text: v }))
      opts.push({ value: 'Nuevo proveedor', text: this.$t('new', { type: this.$t('supplier') }) })
      return opts
    },

    statusOptions() {
      return ['paid', 'credit', 'topay', 'expired'].map(value => ({ value, text: this.$t(value) }))
    },

    unitOptions() {
      return [
        { value: null, text: this.$t('expenseCapture.pickUnit') },
        ...UNIT_KEYS.map(value => ({ value, text: this.$tc(this.unitsFilter(value), 2) })),
      ]
    },

    newMaterialValid() {
      return this.newMaterial.name.trim().length > 0 && Boolean(this.newMaterial.unit_type)
    },

    subtotal() {
      return this.rows.reduce((sum, row) => sum + this.rowTotal(row), 0)
    },

    vat() {
      return this.subtotal * ((Number(this.header.iva_percentage) || 0) / 100)
    },

    grandTotal() {
      return this.subtotal + this.vat
    },
  },

  async mounted() {
    await this.fetchExpensesTypeCatalogs(this.$route.params.projectId).then(response => {
      const material = response.data.find(expense => expense.name === 'Material')
      this.materialCatalogId = material ? material.id : null
    })
    await this.fetchSuppliers({ projectId: this.$route.params.projectId }).then(response => {
      this.suppliersResults = response
    })
    await this.fetchGroupedExpenses({ projectId: this.$route.params.projectId })

    if (!this.rows.length) this.addRow(false)
  },

  methods: {
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('expenses', ['addExpense', 'fetchSuppliers']),
    ...mapActions('groupedExpenses', ['fetchExpensesTypeCatalogs', 'fetchGroupedExpenses']),

    onHeaderCategory(name) {
      this.headerCategoryName = name
      this.rows.forEach(row => {
        if (!row.category && !this.isMaterialRow(row)) this.resetMaterial(row)
      })
    },

    effectiveCategoryId(row) {
      return row.category ? row.category.id : this.header.expense_type_catalog_id
    },

    effectiveCategoryName(row) {
      return row.category ? row.category.name : this.headerCategoryName
    },

    isMaterialRow(row) {
      return Boolean(this.materialCatalogId) && this.effectiveCategoryId(row) === this.materialCatalogId
    },

    openRowCategory(row) {
      this.rowCategoryRow = row
      this.rowCategoryDraft = { expense_type_catalog_id: null }
      this.rowCategoryHistory = []
      this.rowCategoryDraftName = ''
      this.$bvModal.show('rowCategory')
    },

    confirmRowCategory() {
      const row = this.rowCategoryRow
      if (!row || !this.rowCategoryDraft.expense_type_catalog_id) return

      const id = this.rowCategoryDraft.expense_type_catalog_id
      const name = this.rowCategoryDraftName
        || (this.rowCategoryHistory[this.rowCategoryHistory.length - 1] || {}).name
        || ''

      row.category = id === this.header.expense_type_catalog_id ? null : { id, name }

      if (!this.isMaterialRow(row)) this.resetMaterial(row)
    },

    blankRow() {
      rowSequence += 1
      const row = {
        uid: `row-${rowSequence}`,
        query: '',
        results: [],
        searching: false,
        name: '',
        sku: '',
        brand: '',
        description: '',
        unit_type: null,
        quantity: null,
        unit_price: null,
        isNewMaterial: false,
        category: null,
      }
      row.search = _.debounce(query => this.searchProducts(row, query), 400)
      return row
    },

    addRow(focus = true) {
      const row = this.blankRow()
      this.rows.push(row)
      if (focus) this.$nextTick(() => this.focusRow(row))
    },

    removeRow(index) {
      if (this.rows.length === 1) return
      this.rows.splice(index, 1)
    },

    focusRow(row) {
      const refName = this.isMaterialRow(row) ? `search-${row.uid}` : `concept-${row.uid}`
      const ref = this.$refs[refName]
      const component = Array.isArray(ref) ? ref[0] : ref
      if (!component) return

      const input = component.$el && component.$el.tagName === 'INPUT'
        ? component.$el
        : component.$el && component.$el.querySelector('input')
      if (input) input.focus()
    },

    onPriceEnter(index) {
      if (index === this.rows.length - 1) {
        this.addRow()
      } else {
        this.focusRow(this.rows[index + 1])
      }
    },

    selectAll(event) {
      if (event && event.target && typeof event.target.select === 'function') event.target.select()
    },

    rowTotal(row) {
      const total = (Number(row.quantity) || 0) * (Number(row.unit_price) || 0)
      return Number.isFinite(total) ? total : 0
    },

    unitLabel(row) {
      const key = this.unitsFilter(row.unit_type)
      return key ? this.$tc(key, Number(row.quantity) || 1) : '—'
    },

    onQueryInput(row, value) {
      row.query = value
      if (row.name && value !== row.name) this.resetMaterial(row, false)
      row.searching = value.trim().length >= 2
      row.search(value)
    },

    searchProducts(row, query) {
      if (!query.trim().length) {
        row.searching = false
        row.results = []
        return
      }

      this.fetchProducts({
        by_name: query,
        by_active_status: true,
        meta: { pagination: { per_page: 10 } },
      }).then(response => {
        if (row.query === query) row.results = JSON.parse(JSON.stringify(response.data))
      }).finally(() => {
        if (row.query === query) row.searching = false
      })
    },

    canOfferNewMaterial(row) {
      return row.query.trim().length >= 2 && !row.searching
    },

    searchOptions(row) {
      const options = [...row.results, ...this.groupedExpenses]
      const query = row.query.trim()
      if (query.length < 2) return options
      return [{ name: query, isNewMaterialOption: true }, ...options]
    },

    noMatches(row) {
      const query = row.query.trim().toLowerCase()
      if (query.length < 2 || row.searching) return false

      const matches = item => String(item?.name || '').toLowerCase().includes(query)
        || String(item?.sku || '').toLowerCase().includes(query)

      return !row.results.some(matches) && !this.groupedExpenses.some(matches)
    },

    selectProduct(row, product) {
      if (product.isNewMaterialOption) {
        this.openNewMaterial(row)
        return
      }
      row.name = product.name
      row.query = product.name
      row.sku = product.sku || ''
      row.brand = /^(n\/a|na|-)$/i.test(String(product.brand || '').trim()) ? '' : (product.brand || '')
      row.description = product.description || ''
      row.unit_type = product.unit_type || null
      row.isNewMaterial = false
    },

    resetMaterial(row, clearQuery = true) {
      row.name = ''
      row.sku = ''
      row.brand = ''
      row.unit_type = null
      row.isNewMaterial = false
      if (clearQuery) row.query = ''
    },

    openNewMaterial(row) {
      this.newMaterialRow = row
      this.newMaterial = {
        name: row.query.trim(), unit_type: null, brand: '', description: '',
      }
      this.$bvModal.show('newMaterial')
    },

    confirmNewMaterial(event) {
      if (!this.newMaterialValid) {
        event.preventDefault()
        return
      }
      const row = this.newMaterialRow
      if (!row) return

      row.name = this.newMaterial.name.trim()
      row.query = row.name
      row.sku = ''
      row.brand = this.newMaterial.brand.trim()
      row.description = this.newMaterial.description.trim()
      row.unit_type = this.newMaterial.unit_type
      row.isNewMaterial = true
    },

    buildRecords() {
      const supplier = this.header.supplier === 'Nuevo proveedor'
        ? this.header.new_supplier.trim()
        : this.header.supplier

      return this.rows.map(row => {
        const material = this.isMaterialRow(row)
        return {
          expense_type_catalog_id: this.effectiveCategoryId(row),
          name: material ? row.name : this.effectiveCategoryName(row),
          description: row.description,
          brand: material ? row.brand : '',
          sku: material ? row.sku : '',
          unit_type: material ? row.unit_type : null,
          quantity: row.quantity,
          unit_price: row.unit_price,
          iva_percentage: this.header.iva_percentage,
          supplier,
          expense_status: this.header.expense_status,
          expense_date: expenseDateForApi(this.header.expense_date, this.header.expense_time),
          expense_create_type: this.$route.query.createType,
          material_suggestion: material && row.isNewMaterial,
        }
      })
    },

    async submit() {
      this.serverErrors = []

      const valid = await this.$refs.expensesValidation.validate()
      if (!this.header.expense_type_catalog_id) {
        this.serverErrors = [this.$t('expenseCapture.pickCategoryFirst')]
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }
      if (!valid) return

      this.saving = true
      try {
        await this.addExpense({
          project_id: this.$route.params.projectId,
          expense_records: this.buildRecords(),
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$tc('expenseCapture.saved', this.rows.length, { count: this.rows.length }),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$router.push({ name: 'allExpenses', params: { projectId: this.$route.params.projectId } })
      } catch (error) {
        const messages = error?.data?.messages
        this.serverErrors = Array.isArray(messages) && messages.length
          ? messages
          : [this.$t('expenseCapture.genericError')]
      } finally {
        this.saving = false
      }
    },

    cancel() {
      this.$router.push({ name: 'allExpenses', params: { projectId: this.$route.params.projectId } })
    },

    saveMobile(records) {
      if (this.saving) return
      this.saving = true

      this.addExpense({ project_id: this.$route.params.projectId, expense_records: records })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('expenseAdded'), icon: 'SendIcon', variant: 'success' },
          })
          this.$router.push({ name: 'allExpenses', params: { projectId: this.$route.params.projectId } })
        })
        .catch(() => {
          this.saving = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
$line: #ebe9f1;
$ink: #25233a;
$muted: #6e6b7b;
$primary: #340668;
$ok: #1f9d63;

.ticket-head {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 1.5rem 2rem;
}

.ticket-head__label {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: $muted;
  margin-bottom: 0.5rem;
}

.ticket-head__ready {
  display: inline-flex;
  align-items: center;
  margin-top: 0.75rem;
  padding: 0.4rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(31, 157, 99, 0.1);
  color: $ok;
  font-size: 0.85rem;
}

.ticket-head__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 1rem;
  align-content: start;
}

.lines-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid $line;
}

.lines-head__count {
  min-width: 2rem;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  background: #f3f2f7;
  color: $ink;
  font-weight: 700;
  text-align: center;
}

.lines-table-wrap {
  overflow-x: auto;
}

.lines-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.85rem 0.75rem;
    vertical-align: top;
    border-bottom: 1px solid $line;
  }

  th {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: $muted;
    background: #f8f8fa;
    white-space: nowrap;
  }

  th:first-child,
  td:first-child {
    padding-left: 1.5rem;
  }

  th:last-child,
  td:last-child {
    padding-right: 1rem;
  }

  tr.is-new-material td {
    background: #fffcf3;
  }
}

.lines-table__num {
  width: 2.5rem;
  color: $muted;
  font-variant-numeric: tabular-nums;
  padding-top: 1.35rem !important;
}

.lines-table__qty {
  width: 8rem;
}

.lines-table__unit {
  width: 10rem;
}

.lines-table__price {
  width: 11rem;
}

.lines-table__total {
  width: 9rem;
  text-align: right;
}

.lines-table__actions {
  width: 3rem;
  text-align: right;
}

.line-search.is-invalid ::v-deep input {
  border-color: #ea5455;
}

.line-search ::v-deep .list-group {
  z-index: 20;
}

.line-search__new {
  color: #340668;
  font-weight: 600;
}

.line-search__suggestion {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  font-size: 0.85rem;
}

.line-search__sku {
  font-size: 0.7rem;
  font-weight: 700;
  color: $muted;
  white-space: nowrap;
}

.line-meta {
  margin-top: 0.35rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.line-meta__catalog {
  color: $ok;
  font-weight: 600;
}

.line-meta__new {
  color: #b7791f;
  font-weight: 600;
}

.line-not-found {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  padding: 0.55rem 0.75rem;
  border: 1px dashed #c9c4d6;
  border-radius: 0.5rem;
  background: #faf9fc;
  font-size: 0.82rem;
  color: #5e5a6e;
}

.line-category {
  display: inline-flex;
  align-items: center;
  margin-top: 0.4rem;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.72rem;
  color: $muted;

  &:hover .line-category__change {
    text-decoration: underline;
  }
}

.line-category__change {
  margin-left: 0.25rem;
  color: $primary;
  font-weight: 600;
}

.line-unit {
  display: inline-block;
  padding-top: 0.55rem;
  color: $ink;
}

.line-total {
  display: inline-block;
  padding-top: 0.55rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: $ink;
}

.line-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  margin-top: 0.3rem;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: $muted;

  &:hover:not(:disabled) {
    background: #fdecec;
    color: #ea5455;
  }

  &:disabled {
    opacity: 0.35;
  }
}

.lines-foot {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.5rem 1.25rem;
}

.totals {
  min-width: 16rem;
}

.totals__row {
  display: flex;
  justify-content: space-between;
  padding: 0.3rem 0;
  color: $muted;
  font-variant-numeric: tabular-nums;
}

.totals__row--grand {
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid $line;
  color: $ink;
  font-size: 1.05rem;
  font-weight: 700;
}

.new-material__note {
  display: flex;
  align-items: flex-start;
  padding: 0.6rem 0.75rem;
  font-size: 0.85rem;
}

@media (max-width: 991.98px) {
  .ticket-head {
    grid-template-columns: 1fr;
  }
}
</style>
